import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import TickSVG from "@krea/customer-features/images/svg/checkmark-with-background.svg";
import { Toast } from "@krea/customer-features/shared-components/toast/Toast";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { IFieldRequest } from "@krea/customer-features/types/fieldRequest";
import {
  ICompanyLoanBid,
  IInvoicePurchaseBid,
  ILeasingBid,
  ILenderResponse,
  IRealEstateBid,
  IRevolvingCreditBid,
} from "@krea/customer-features/types/lenderResponse";
import { RATE_TO_BID_KEY_MAP } from "@krea/customer-features/types/loanApplication";
import { BID_TYPE } from "@krea/customer-features/utils";

import CompanyLoanBidItem from "../company-loan-bids/CompanyLoanBidItem";
import FactoringBidItem from "../factoring-bids/FactoringBidItem";
import LeasingBidItem from "../leasing-bids/LeasingBidItem";
import RealEstateBidItem from "../real-estate-bids/RealEstateBidItem";
import RevolvingCreditBidItem from "../revolving-credit-bids/RevolvingCreditBidItem";

interface ISelectedBidItem {
  bid:
    | ICompanyLoanBid
    | IInvoicePurchaseBid
    | IRealEstateBid
    | IRevolvingCreditBid
    | ILeasingBid;
  bidType: BID_TYPE;
  onSelectLenderResponseClick: (lenderResponse: ILenderResponse) => void;
  isPaidOutPage?: boolean;
}

const SelectedBidItem = ({
  bid,
  bidType,
  onSelectLenderResponseClick,
  isPaidOutPage = false,
}: ISelectedBidItem) => {
  const { t } = useTranslation();
  const isRecommendedSelect = bid.lenderResponse.isRecommendedSelect;
  const { data } = useGetCachedCurrentLoanApplication();
  const { customerPreferredRate } = data ?? {};

  const isPaidOut = !!bid.lenderResponse.paidOutAt;

  const bidItem = useCallback((Component: any) => {
    return (
      <Component
        onSelectLenderResponseClick={onSelectLenderResponseClick}
        bid={bid}
        selectedLenderResponseID={bid.lenderResponse.id}
        bidAttractivenessScore={bid.lenderResponse.attractivenessScore}
        rateKey={RATE_TO_BID_KEY_MAP[customerPreferredRate!]}
        isPaidOutPage={isPaidOutPage}
      />
    );
  }, []);

  const companyLoanBidItem = useMemo(() => bidItem(CompanyLoanBidItem), []);
  const realEstateBidItem = useMemo(() => bidItem(RealEstateBidItem), []);
  const revolvingCreditBidItem = useMemo(
    () => bidItem(RevolvingCreditBidItem),
    [],
  );
  const leasingBidItem = useMemo(() => bidItem(LeasingBidItem), []);
  const factoringBidItem = useMemo(() => bidItem(FactoringBidItem), []);

  function getBidItemByType(bidType: BID_TYPE) {
    switch (bidType) {
      case BID_TYPE.COMPANY_LOAN:
        return companyLoanBidItem;
      case BID_TYPE.FACTORING:
        return factoringBidItem;
      case BID_TYPE.LEASING:
        return leasingBidItem;
      case BID_TYPE.REAL_ESTATE:
        return realEstateBidItem;
      case BID_TYPE.REVOLVING_CREDIT:
        return revolvingCreditBidItem;
      default:
        return null;
    }
  }

  function getLoanTypeName(bidType: BID_TYPE) {
    switch (bidType) {
      case BID_TYPE.COMPANY_LOAN:
        return t("dash.loanBids.heading");
      case BID_TYPE.FACTORING:
        return t("dash.factoringBids.heading");
      case BID_TYPE.LEASING:
        return t("dash.leasingBids.heading");
      case BID_TYPE.REAL_ESTATE:
        return t("dash.realEstateBids.heading");
      case BID_TYPE.REVOLVING_CREDIT:
        return t("dash.revolvingCreditBids.heading");
      default:
        return "";
    }
  }

  const loanType = getLoanTypeName(bidType).toLowerCase();
  const lenderCompanyName =
    bid.lenderResponse.lenderApplication.lenderCompany.name;

  return (
    <>
      {!isPaidOut && isRecommendedSelect && (
        <Toast
          innerText={{
            title: t("recommendedSelect.toast.heading", {
              loanType,
              lenderCompanyName,
            }),
            subtitle: t("recommendedSelect.toast.subheading"),
          }}
          icon={TickSVG}
        />
      )}

      {getBidItemByType(bidType)}
    </>
  );
};

export default SelectedBidItem;
